import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API, saveSession, getAuthHeaders, errorText } from '@/utils/api';

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Prevent double execution in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        // Extract session_token from URL fragment (set by backend Google OAuth callback)
        const hash = location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionToken = params.get('session_token');

        if (!sessionToken) {
          navigate('/login');
          return;
        }

        // Save session token to localStorage
        saveSession(sessionToken, null);

        // Fetch user data using the session token
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        });

        // Save full session with user data
        saveSession(sessionToken, response.data);

        // Navigate to dashboard with user data
        navigate('/dashboard', { state: { user: response.data }, replace: true });
      } catch (error) {
        console.error('Auth callback error:', error);
        if (error.response?.status === 403) {
          setError(error.response.data?.detail || 'Access denied. Your email is not registered.');
        } else {
          setError(errorText(error) || error.message || 'Authentication failed. Please try again.');
        }
      }
    };

    processSession();
  }, [navigate, location.hash]);

  // Show access denied or other error
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="max-w-md p-8 bg-white rounded-lg shadow-lg border border-red-200">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-red-800 mb-2">Login Failed</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-xs text-gray-400 mb-6">If this issue persists, please contact your administrator.</p>
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-muted-foreground">Authenticating...</p>
      </div>
    </div>
  );
}
